// @ts-nocheck
import { isEmpty, isEqual } from 'lodash';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { Map as MapComponent } from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { ButtonUI, Selector, SelectorOptionPropsInterface, usePrevious } from 'sonata-react-components';

import { reportingSetCartographyMapConfig, setReportingCartographyFilterMap } from '@/store/actions/reporting.actions';
import {
  reportAdvancedFilterSetCartographyCountries,
  reportAdvancedFilterSetCartographyLevel,
  reportAdvancedFilterSetCartographyLevelIds,
  reportingAdvancedFilterResetCartography
} from '@/store/actions/reportingAdvancedFilter.actions';
import { getCountriesWithEvents } from '@/store/selectors/reporting.selectors';
import {
  getCampaigns,
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getSection
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { useCartography } from '@/ts-modules/reporting/reporting.hooks';
import { ButtonsContainer } from './cartographyFilter.style';

type Props = {
  mapId: MapComponent['id'];
};
export function CartographyFilter({ mapId }: Props) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const cids = useSelector(getCampaigns);
  const generalCartographyLevel = useSelector(getCartographyLevel);
  const generalCartographyLevelIds = useSelector(getCartographyLevelIds);
  const campaignCountries = useSelector(getCountriesWithEvents) || [];
  // @ts-ignore
  const reportingMapCountries = useSelector(store => getCartographyCountries(store, 'maps', mapId));
  // @ts-ignore
  const generalReportingCountries = useSelector(store => getCartographyCountries(store, 'general'));
  // @ts-ignore
  const generalSectionSettings = useSelector(store => getSection(store, 'general'));
  const reportingCountries = reportingMapCountries.length
    ? reportingMapCountries
    : generalReportingCountries.length
      ? generalReportingCountries
      : campaignCountries;

  const countryToken = {};
  const levelToken = {};

  const { getMap, resetMapCartographyFilter, setMapCartographyFilter, setMapSettings } = useReportingMap();

  const map = getMap({ id: mapId });
  const {
    cartographyFilter,
    settings,
    settings: {
      cartography: { countries: cartographySettingsCountries }
    },
    type
  } = map!;

  const country = cartographySettingsCountries[0];

  const { countries = [], level, shapeIds } = cartographyFilter;

  const prevCountries = usePrevious(countries);
  const prevCountry = usePrevious(country);
  const prevLevel = usePrevious(level);

  useEffect(() => {
    if (prevCountries?.length && !isEqual(countries, prevCountries)) {
      setMapCartographyFilter({
        id: mapId,
        value: {
          ...cartographyFilter,
          level: '',
          shapeIds: []
        }
      });
    }
  }, [countries]);

  useEffect(() => {
    if (prevLevel && !isEqual(level, prevLevel)) {
      setMapCartographyFilter({
        id: mapId,
        value: {
          ...cartographyFilter,
          shapeIds: []
        }
      });
    }
  }, [level]);

  const countryToFilter = countries.length > 0 ? countries : reportingCountries;

  const [data] = useCartography({
    config: {
      cids,
      countries: campaignCountries,
      countryToken,
      levelToken
    },
    filters: {
      country: countryToFilter,
      end: generalSectionSettings.timeFrame.endDate,
      items: {
        level: generalCartographyLevel,
        levelIds: generalCartographyLevelIds
      },
      level,
      start: generalSectionSettings.timeFrame.beginDate,
      timezone: generalSectionSettings.timeZone
    }
  });

  const { countries: cartographyCountries, items: cartographyItems, levels: cartographyLevels } = data;
  const shouldBeDisabled = countries.length ? countries.length > 1 : reportingCountries.length > 1;

  const levelOptions = generalCartographyLevel
    ? cartographyLevels.slice(cartographyLevels.findIndex(option => option.id === generalCartographyLevel) + 1)
    : cartographyLevels;

  const onCountryChange = (values: Record<string, any>[]) => {
    const newValues = values.map(value => value.id);
    setMapCartographyFilter({
      id: mapId,
      value: {
        ...cartographyFilter,
        countries: newValues
      }
    });
  };

  const onLevelChange = (value: Record<string, any>) => {
    setMapCartographyFilter({
      id: mapId,
      value: {
        ...cartographyFilter,
        level: value.id
      }
    });
  };

  const onItemChange = (values: Record<string, any>[]) => {
    const newValues = values.map(value => value.id);
    setMapCartographyFilter({
      id: mapId,
      value: {
        ...cartographyFilter,
        shapeIds: newValues
      }
    });
  };

  const onApplyFilter = () => {
    dispatch(reportAdvancedFilterSetCartographyCountries(countries, 'maps', mapId));
    dispatch(reportAdvancedFilterSetCartographyLevel(level, 'maps', mapId));
    dispatch(reportAdvancedFilterSetCartographyLevelIds(shapeIds, 'maps', mapId));
    dispatch(setReportingCartographyFilterMap(mapId));
    setMapSettings({
      id: mapId,
      value: {
        ...settings,
        cartography: {
          ...settings.cartography,
          status: 'reload'
        }
      }
    });
    dispatch(
      reportingSetCartographyMapConfig({
        filter: cartographyFilter
      })
    );
  };

  const onResetFilter = () => {
    resetMapCartographyFilter({ id: mapId });
    setMapSettings({
      id: mapId,
      value: {
        ...settings,
        cartography: {
          ...settings.cartography,
          status: 'reload'
        }
      }
    });
    dispatch(
      reportingSetCartographyMapConfig({
        filter: {}
      })
    );
    dispatch(reportingAdvancedFilterResetCartography('maps', mapId));
    dispatch(setReportingCartographyFilterMap(mapId));
  };

  const countryValue = cartographyCountries.reduce((acc: Record<string, any>[], actualItem) => {
    const newCountries = country && type === 'cartography' ? [country] : countries;
    const normalizedCountries = newCountries.map(c => c.toUpperCase());
    if (normalizedCountries.includes(actualItem?.id)) {
      return [...acc, actualItem];
    }
    return acc;
  }, []);

  useEffect(() => {
    if (!isEmpty(country) && !isEqual(country, prevCountry) && type === 'cartography') {
      onCountryChange([{ id: country }]);
    }
  }, [country, onCountryChange]);

  return (
    <div className="mapInnerControls__section" id={`cartographyFilter_${mapId}`}>
      {campaignCountries.length > 1 && (
        <Selector
          isDisabled={!!(country && type === 'cartography')}
          label={formatMessage({
            defaultMessage: 'Country',
            id: 'reporting.map.innerControls.cartographyFilter.selectors.country.label'
          })}
          onChange={onCountryChange}
          options={cartographyCountries as SelectorOptionPropsInterface[]}
          placeholder={formatMessage({
            defaultMessage: 'Select countries...',
            id: 'reporting.map.innerControls.cartographyFilter.selectors.country.placeholder'
          })}
          type="multiple"
          value={countryValue as SelectorOptionPropsInterface[]}
        />
      )}
      <Selector
        isClearable
        isDisabled={shouldBeDisabled || cartographyLevels.length === 0}
        label={formatMessage({
          defaultMessage: 'Cartography level',
          id: 'reporting.map.innerControls.cartographyFilter.cartographyLevelSelectorLabel'
        })}
        onChange={onLevelChange}
        options={levelOptions as SelectorOptionPropsInterface[]}
        type="simple"
        value={cartographyLevels.filter(l => l.id === level) as SelectorOptionPropsInterface[]}
      />
      <Selector
        isClearable
        isDisabled={shouldBeDisabled || cartographyItems.length === 0 || !level}
        label={formatMessage({
          defaultMessage: 'Cartography items',
          id: 'reporting.map.innerControls.cartographyFilter.cartographyItemsSelectorLabel'
        })}
        onChange={onItemChange}
        options={cartographyItems as SelectorOptionPropsInterface[]}
        type="multiple"
        value={cartographyItems.filter(i => shapeIds.includes(i.id)) as SelectorOptionPropsInterface[]}
      />
      <ButtonsContainer>
        <ButtonUI
          className="cartographyFilter__trashButton"
          icon="fal fa-trash"
          iconPosition="left"
          onClick={onResetFilter}
        />
        <ButtonUI
          className="cartographyFilter__applyButton"
          disabled={(reportingCountries.length > 1 && !countries.length) || !shapeIds.length || !level}
          onClick={onApplyFilter}
          text={formatMessage({
            defaultMessage: 'Apply filter',
            id: 'reporting.map.innerControls.cartographyFilter.buttons.apply'
          })}
          type="secondary"
        />
      </ButtonsContainer>
    </div>
  );
}
